'use client';

import { useEffect, useState } from 'react';
import { BookOpen, PlayCircle } from 'lucide-react';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import type { HelpArticle } from '@/lib/help/types';

type Props = {
  slug: string;
  onOpen: (slug: string) => void;
};

/**
 * A help article, as a row inside Mucka's bubble.
 *
 * It was a card of its own — a tinted ground, a 36px icon plate, a 14px title,
 * a summary line and an uppercase orange CTA. All of that went when the chat
 * got one container: everything Mucka hands you is a strip in her bubble now,
 * and this is a row with a chevron like a job or a document.
 *
 * 🚨 The SUMMARY went with it, deliberately. She has just said what the
 * article covers in her own reply — the card was repeating her back at herself
 * one line further down, in smaller type.
 *
 * The one thing that earns a second line is a video's LENGTH. An engineer on a
 * doorstep needs to know whether this is two minutes or twenty before he taps,
 * and that is the only fact here he cannot get from what she said.
 *
 * Still fetches its own metadata: the engine embeds a `[[help:slug]]` marker
 * and the title lives in the MDX, so something has to go and get it. Renders
 * nothing if the slug does not resolve — the engine can emit a marker for an
 * article that has since been deleted.
 */
export default function HelpArticleCard({ slug, onOpen }: Props) {
  const [article, setArticle] = useState<HelpArticle | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/help/article/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setArticle(data);
      })
      .catch(() => {
        if (!cancelled) setArticle(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // No skeleton any more: a row is one line high, and a grey bar flashing in
  // the middle of a reply is more disruptive than the row simply arriving.
  if (!article) return null;

  const video = article.video?.id ? article.video : null;

  return (
    <BubbleRows
      rows={[{
        id: slug,
        title: article.title,
        detail: video ? watchLabel(video.duration) : undefined,
        icon: video ? PlayCircle : BookOpen,
        onPress: () => onOpen(slug),
      }]}
    />
  );
}

/** "Watch · 2 min". Rounded up, because a 90-second video is not one minute to
 *  somebody deciding whether they have time for it. Bare "Watch" when the MDX
 *  gives no duration — a made-up length is worse than none. */
function watchLabel(seconds?: number): string {
  if (!seconds || seconds <= 0) return 'Watch';
  return `Watch · ${Math.max(1, Math.ceil(seconds / 60))} min`;
}
